<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from './LanguageSwitcher.vue'
import * as useApi from '@/api/modules/use'
import { useUserStore } from '@/store'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const userStore = useUserStore()

interface Props {
  left?: boolean,
  isSearch?: boolean,
  isLogo?: boolean,
  showNavbar?: boolean,
  showSearch?: boolean
}

const props = defineProps<Props>()

// 定义emit事件
const emit = defineEmits(['search'])

// 根据props计算显示状态
const showLeft = computed(() => props.left === true)
const showLogo = computed(() => props.isLogo !== false) // 默认显示logo
const showNavbar = computed(() => props.showNavbar !== false && !props.isSearch) // 默认显示navbar，除非isSearch为true
const showSearch = computed(() => props.showSearch || props.isSearch) // 显示搜索框

// 搜索关键字
const searchKeyword = ref('')

// 未读数量
const unreadCount = ref(0)

// 格式化未读数量（超过99显示99+）
const formattedUnreadCount = computed(() => {
  return unreadCount.value > 99 ? '99+' : unreadCount.value.toString()
})

const goHome = () => {
  router.push('/')
}

// 处理搜索输入
const handleSearch = () => {
  emit('search', searchKeyword.value)
}

// 处理按键事件
const handleKeyUp = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    handleSearch()
  }
}

// 定时器引用
let pollingTimer: number | null = null
const POLLING_INTERVAL = 2000 // 2秒轮询一次

// 获取未读数量
const fetchUnreadCount = async() => {
  if(!userStore.token) return
  try {
    const res = await useApi.getUnreadCount()
    if ((res as any).success) {
      const newCount = (res as any).content || 0
      if (newCount !== unreadCount.value) {
        // console.log('📊 未读数量更新:', unreadCount.value, '->', newCount)
        unreadCount.value = newCount
      }
    }
  } catch (error) {
    console.error('获取未读数量失败:', error)
  }
}

// 启动轮询
const startPolling = () => {
  // 先清除可能存在的定时器
  stopPolling()

  // 设置定时轮询
  pollingTimer = window.setInterval(() => {
    // 只在非聊天页面轮询（聊天页面有自己的实时更新机制）
    if (route.path !== '/chat') {
      fetchUnreadCount()
    }
  }, POLLING_INTERVAL)

}

// 停止轮询
const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
}

// 处理页面可见性变化
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    fetchUnreadCount()
    // 页面可见时启动轮询
    startPolling()
  } else {
    // 页面隐藏时停止轮询以节省资源
    stopPolling()
  }
}

// 组件挂载时获取未读数量并启动轮询
onMounted(() => {
  // 立即获取一次未读数量
  fetchUnreadCount()

  // 启动轮询
  startPolling()

  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // 监听自定义事件：刷新未读数
  window.addEventListener('refreshUnreadCount', fetchUnreadCount)
})

// 组件卸载时清理
onBeforeUnmount(() => {
  stopPolling()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('refreshUnreadCount', fetchUnreadCount)
})

// 监听路由变化，从聊天页返回时刷新未读数量
watch(() => route.path, (newPath, oldPath) => {
  if (oldPath === '/chat' && newPath !== '/chat') {
    // 从聊天页离开时刷新未读数
    fetchUnreadCount()
  }
})

// 判断是否显示底部导航（移动端只在首页、助教、我的页面显示）
const shouldShowFooter = computed(() => {
  const allowedPaths = ['/', '/chat', '/user', '/task/sign-in-center']
  return allowedPaths.includes(route.path)
})

// 返回上一页
const goBack = () => {
  router.back()
}
</script>

<template>
  <div>
    <div class="public-header md:w-1200px">
      <div v-if="showLeft" class="left">
        <button type="button" class="left__back" @click="goBack">
          <IconLeft />
        </button>
        <div v-if="$slots.leftTitle" class="left__title">
          <slot name="leftTitle" />
        </div>
      </div>
      <img
        v-if="showLogo"
        src="/images/logo.png"
        class="logo"
        @click="goHome"
      />
      <div v-if="showNavbar" class="navbar">
        <div class="label nav-label nav-label--home" :class="{'on': $route.path === '/'}">
          <router-link to="/">
            <div class="icon"></div>
            <span>{{ t('common.home') }}</span>
          </router-link>
        </div>
        <div class="label nav-label nav-label--assistant" :class="{'on': $route.path === '/chat'}">
          <router-link to="/chat">
            <div class="icon"></div>
            <span>{{ t('common.assistant') }}</span>
            <em v-if="unreadCount > 0">{{ formattedUnreadCount }}</em>
          </router-link>
        </div>
        <div
          class="label nav-label nav-label--sign"
          :class="{'on': $route.path === '/task/sign-in-center'}"
        >
          <router-link to="/task/sign-in-center">
            <div class="icon"></div>
            <span>{{ t('common.signIn') }}</span>
          </router-link>
        </div>
        <div class="label nav-label nav-label--mine" :class="{'on': $route.path === '/user'}">
          <router-link to="/user">
            <div class="icon"></div>
            <span>{{ t('common.mine') }}</span>
          </router-link>
        </div>
      </div>
      <div v-if="showSearch" class="search">
        <img src="/images/search.png" @click="handleSearch" />
        <input
          v-model="searchKeyword"
          type="text"
          :placeholder="t('common.searchCourse')"
          @keyup="handleKeyUp"
        />
      </div>
      <LanguageSwitcher class="language-switcher-header" />
    </div>
    <div class="null"></div>
    <div v-show="shouldShowFooter" class="footer">
      <div :class="{'on': $route.path === '/'}" class="footer-item footer-item--home">
        <router-link to="/">
          <i></i>
          <span>{{ t('common.home') }}</span>
        </router-link>
      </div>
      <div :class="{'on': $route.path === '/chat'}" class="footer-item footer-item--assistant">
        <router-link to="/chat">
          <i></i>
          <span>{{ t('common.assistant') }}</span>
          <em v-if="unreadCount > 0">{{ formattedUnreadCount }}</em>
        </router-link>
      </div>
      <div
        class="footer-item footer-item--sign"
        :class="{'on': $route.path === '/task/sign-in-center'}"
      >
        <router-link to="/task/sign-in-center">
          <i></i>
          <span>{{ t('common.signIn') }}</span>
        </router-link>
      </div>
      <div :class="{'on': $route.path === '/user'}" class="footer-item footer-item--mine">
        <router-link to="/user">
          <i></i>
          <span>{{ t('common.mine') }}</span>
        </router-link>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">

	.footer{
		width: 100%;
		height: 1.34rem;
		position: fixed;
		left: 0;
		align-items: center;
		bottom: 0;
		z-index: 9999;
		background: #fff;
		display: flex;
		display: none;
		div{
			flex: 1;
			position: relative;

			a {
				position: relative;

				em {
					position: absolute;
					top: 0;
					right: 25%;
					height: .3rem;
					min-width: .3rem;
					display: inline-flex;
					align-items: center;
					justify-content: center;
					background: #FF4D4F;
					font-size: .22rem;
					color: #fff;
					border-radius: .4rem;
					padding: 0 .08rem;
					line-height: 1;
				}
			}

			&.on{
				span{
					color: #18A3FF;
				}
			}
			&:nth-child(1){
				i{
					background-image: url(../../public/images/footericon1.png);
				}
				&.on{
					i{
						background-image: url(../../public/images/footericon2.png);
					}
				}
			}
			&:nth-child(2){
				i{
					background-image: url(../../public/images/footericon3.png);
				}
				&.on{
					i{
						background-image: url(../../public/images/footericon4.png);
					}
				}
			}
			&:nth-child(3){
				i{
					background-image: url(../../public/images/footericon5.png);
				}
				&.on{
					i{
						background-image: url(../../public/images/footericon6.png);
					}
				}
			}
      &.footer-item--home{
				i{
					background-image: url(../../public/images/footericon1.png);
				}
				&.on i{
					background-image: url(../../public/images/footericon2.png);
				}
			}
			&.footer-item--assistant{
				i{
					background-image: url(../../public/images/footericon3.png);
				}
				&.on i{
					background-image: url(../../public/images/footericon4.png);
				}
			}
			&.footer-item--sign{
				i{
					background-image: url('../../public/images/checkin.png');
				}
        &.on i{
					background-image: url(../../public/images/checkin-s.png);
				}
			}
			&.footer-item--mine{
				i{
					background-image: url(../../public/images/footericon5.png);
				}
				&.on i{
					background-image: url(../../public/images/footericon6.png);
				}
			}
			i{
				width: .64rem;
				height: .64rem;
				margin: 0 auto;
			display: block;
			background-size: 100% 100%;
			}
			span{
				display: block;
				text-align: center;
				font-size: .24rem;
				color: #8D96A8;
				margin-top: .08rem;
			}
		}
	}
	.language-switcher-header {
		margin-left: 20px;
	}

	.public-header {
		height: 72px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin: 0 auto;
		position: relative;

		.left {
			display: flex;
			align-items: center;
		}
		.left__back {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 24px;
			height: 24px;
			padding: 0;
			border: 0;
			background: transparent;
			color: #1d2540;
			cursor: pointer;

			.arco-icon {
				width: 16px;
				height: 16px;
			}
		}
		.left__title {
			color: #12172A;
			font-size: 16px;
			font-weight: 700;
			line-height: 24px;
		}
		.logo {
			display: block;
			cursor: pointer;
		}
		.search{
			width: 273px;
			height: 34px;
			background: #FFFFFF;
			border-radius: 8px 8px 8px 8px;
			opacity: 0.5;
			display: flex;
			align-items: center;
			padding-left: 10px;
			position: absolute;
			right: 0;
			top: 57px;

			img{
				width: 12px;
			}
			input{
				flex: 1;
				height: 100%;
				font-size: 14px;
				color: #333;
				border: none;
				background: none;
				padding-left: 6px;
				&::placeholder{
					color: #BDC3D1;
				}
			}
		}
		.navbar {
			height: 100%;
			display: flex;
			align-items: center;
			.label {
				height: 100%;
				margin-left: 40px;
				position: relative;
				&::after {
					content: '';
					width: 0;
					height: 2px;
					background: #3E94FF;
					transition: all .2s;
					border-radius: 10px 10px 10px 10px;
					position: absolute;
					left: 50%;
					transform: translateX(-50%);
					bottom: 20px;
				}
				&:nth-child(2) {
					.icon {
						background-image: url(/images/index4.png);
					}
				}
				&:nth-child(3) {
					.icon {
						background-image: url(/images/index3.png);
					}
				}
				&.on {
					span {
						color: #3E94FF;
					}
					&::after {
						width: 20px;
					}
				}
				&:hover {
					span {
						color: #3E94FF;
					}
					&::after {
						width: 20px;
					}
					&:nth-child(1) {
						.icon {
							background-image: url(/images/index5.png);
						}
					}
					&:nth-child(2) {
						.icon {
							background-image: url(/images/chats1.png);
						}
						em{
							background: rgba(62, 148, 255, .3);
						}
					}
					&:nth-child(3) {
						.icon {
							background-image: url(/images/useractive.png);
						}
					}
				}
				&:nth-child(1) {
					&.on {
						.icon {
							background-image: url(/images/index5.png);
						}
					}
				}
				&:nth-child(2) {
					&.on {
						.icon {
							background-image: url(/images/chats1.png);
						}
						em{
							background: rgba(62, 148, 255, .3);
						}
					}
				}
				&:nth-child(3) {
					&.on {
						.icon {
							background-image: url(/images/useractive.png);
						}
					}
				}
        &.nav-label--home {
					.icon {
						background-image: url(/images/chats2.png);
					}
					&.on .icon,
					&:hover .icon {
						background-image: url(/images/index5.png);
					}
				}
				&.nav-label--assistant {
					.icon {
						background-image: url(/images/index4.png);
					}
					&.on .icon,
					&:hover .icon {
						background-image: url(/images/chats1.png);
					}
				}
				&.nav-label--sign {
					.icon {
						background-image: url('/images/checkin-top.png');
					}
          &.on .icon,
					&:hover .icon {
						background-image: url(/images/checkin-top-s.png);
					}
				}
				&.nav-label--mine {
					.icon {
						background-image: url(/images/index3.png);
					}
					&.on .icon,
					&:hover .icon {
						background-image: url(/images/useractive.png);
					}
				}
				a {
					display: flex;
					align-items: center;
					height: 100%;
					em {
						height: 15px;
						display: inline-flex;
						align-items: center;
						justify-content: center;
						background: rgba(141, 150, 168, .3);
						font-size: 10px;
						color: #fff;
						border-radius: 40px;
						padding: 0 2px;
						line-height: 1;
						min-width: 15px;
						margin-left: 6px;
					}
					.icon {
						width: 16px;
						height: 16px;
						background-size: 100%;
						background-repeat: no-repeat;
						background-position: center center;
						background-image: url(/images/chats2.png);
					}
					span {
						font-size: 14px;
						margin-left: 4px;
						color: #8D96A8;
					}
				}
			}
		}
	}
	@media (max-width: 1400px) {
		.public-header{
			width: 1000px !important;
		}

	}
	@media (max-width: 1200px) {
		.public-header{
			width: 100% !important;
			padding: 0 6%;
		}

	}
	@media (max-width: 1000px) {
		.public-header{
			height: 1rem !important;
			background-image: url(../../public/images/indexbg.png);
			position: fixed;
			left: 0;
			z-index: 99;
			top: 0;
		}
		.search{
			position: initial !important;
		}
		.public-header{
			width: 100% !important;
			padding: 0 0.3rem;
		}
		.language-switcher-header{
			margin-left: .2rem;
			padding: 0;
			min-width: 1.4rem;
		}
		.public-header .navbar{
			display: none;
		}
		.null{
			height: 1rem;
		}
		.public-header .logo{
			width: 1.4rem;
		}
		.footer{
			display: flex;
		}
	}
</style>